import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { apiClient, orgHeader, unwrap } from "../../../api/client";
import type { CreavisionDashboardDocument } from "../../../documents/generated/dashboard";
import { formatAge } from "../../../i18n/format";
import { Button } from "../../../ui/Button";
import type { Widget, WidgetInput } from "../../../widgets/types";
import { DashboardEditor } from "../../dashboards/DashboardEditor";
import { saveErrors } from "../../dashboards/errors";
import { AddToDashboard, CreateFromDevice } from "../../dashboards/FromDevice";
import { ConfirmDialog } from "../../ops/shared/ConfirmDialog";
import type { DeviceActions } from "../actions/useDeviceActions";
import { addNewWidgets, missingKeys, notOffered, toSave } from "./savedPage";
import { type WidgetPage } from "./queries";
import { neededPlan } from "../../dashboards/planWidgets";
import { WidgetGrid } from "./WidgetGrid";
import { LayoutEdit } from "../../../ui/LayoutEdit";

type Layer = "personal" | "organisation";

/**
 * The device page's grid with its layer, Edit page, Save for me or the organisation, and Reset (device-dashboards
 * WEB-DDB-SAVE-01, WEB-DDB-EDIT-01, -03). `saved` is the page in effect from the server; without it (a server
 * without the device page routes) the profile's default is drawn and cannot be edited.
 */
export function PageGrid({
  orgId,
  deviceId,
  name,
  page,
  saved,
  grid,
  texts,
  input,
  actions,
  now,
}: {
  orgId: string;
  deviceId: string;
  name: string;
  page: WidgetPage;
  saved: CreavisionDashboardDocument | undefined;
  grid: Widget[];
  texts: Record<string, string>;
  input: WidgetInput;
  actions: DeviceActions;
  now: Date;
}) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Widget[]>();
  const [choosing, setChoosing] = useState<CreavisionDashboardDocument>();
  const [resetting, setResetting] = useState<Layer>();

  // A page applies to every unit of the type, so every cached device page is read again.
  const done = (next: WidgetPage) => {
    void qc.invalidateQueries({ queryKey: ["org", orgId, "/v1/devices/{device_id}/page"] });
    qc.setQueryData(["org", orgId, "/v1/devices/{device_id}/page", deviceId, i18n.language], next);
    setEditing(undefined);
    setChoosing(undefined);
  };
  const save = useMutation({
    mutationFn: ({ layer, document }: { layer: Layer; document: CreavisionDashboardDocument }) =>
      unwrap(
        apiClient("customers").PUT("/v1/devices/{device_id}/page/layout", {
          params: { path: { device_id: deviceId }, header: { "Accept-Language": i18n.language, ...orgHeader(orgId) } },
          body: { layer, document: document as unknown as Record<string, unknown> },
        }),
      ),
    onSuccess: done,
  });
  const reset = useMutation({
    mutationFn: (layer: Layer) =>
      unwrap(
        apiClient("customers").DELETE("/v1/devices/{device_id}/page/layout", {
          params: {
            path: { device_id: deviceId },
            query: { layer },
            header: { "Accept-Language": i18n.language, ...orgHeader(orgId) },
          },
        }),
      ),
    onSuccess: done,
  });

  const fresh = saved && page.layer !== "profile" ? (page.new_widgets ?? []) : [];
  const missing = (w: Widget) => missingKeys(w, deviceId, page).length > 0;
  const edit = (widgets: Widget[]) => {
    save.reset();
    setEditing(widgets);
  };
  function onSave(doc: CreavisionDashboardDocument) {
    if (!saved) return;
    const document = toSave(saved, doc.widgets as unknown as Widget[], deviceId, texts);
    if (page.can_edit_organisation) setChoosing(document);
    else save.mutate({ layer: "personal", document });
  }
  const widgetName = (i: number) => editing?.[i]?.title ?? (editing?.[i] ? `#${i + 1}` : undefined);

  if (editing && saved)
    return (
      <>
        <DashboardEditor
          doc={{ ...saved, name, widgets: editing as unknown as CreavisionDashboardDocument["widgets"] }}
          device={deviceId}
          orgId={orgId}
          input={input}
          inputFor={() => input}
          now={now}
          saving={save.isPending}
          errors={save.error ? saveErrors(save.error, t, widgetName) : undefined}
          notice={(w) => (notOffered(w, deviceId, page).length ? t("devicePage.notOffered") : undefined)}
          onSave={onSave}
          onCancel={() => setEditing(undefined)}
        />
        <LayerDialog
          open={!!choosing}
          busy={save.isPending}
          onPick={(layer) => choosing && save.mutate({ layer, document: choosing })}
          onClose={() => setChoosing(undefined)}
        />
      </>
    );

  return (
    <>
      <div className="mt-6 flex flex-wrap items-center gap-2">
        {saved && (
          <p className="text-sm text-gray-800">
            {t(`devicePage.layer.${page.layer}`)}
            {page.updated_at ? ` · ${formatAge(page.updated_at, i18n.language, now)}` : ""}
          </p>
        )}
        <div className="ml-auto flex flex-wrap gap-2">
          {saved && (
            <LayoutEdit>
              <Button variant="secondary" onClick={() => edit(grid)}>
                {t("devicePage.edit")}
              </Button>
            </LayoutEdit>
          )}
          {saved && page.layer === "personal" && (
            <Button variant="secondary" onClick={() => setResetting("personal")}>
              {t("devicePage.resetMine")}
            </Button>
          )}
          {saved && page.layer === "organisation" && page.can_edit_organisation && (
            <Button variant="secondary" onClick={() => setResetting("organisation")}>
              {t("devicePage.resetOrganisation")}
            </Button>
          )}
          <CreateFromDevice orgId={orgId} name={name} widgets={grid} />
        </div>
      </div>
      {fresh.length > 0 && (
        <p className="mt-3 flex flex-wrap items-center gap-2 rounded-md bg-blue-50 px-3 py-2 text-sm text-gray-900">
          {t("devicePage.newWidgets", { count: fresh.length })}
          <Button variant="secondary" onClick={() => edit(addNewWidgets(grid, fresh, deviceId, texts))}>
            {t("devicePage.addNewWidgets")}
          </Button>
        </p>
      )}
      {reset.isError && (
        <p role="alert" className="mt-2 text-sm text-red-900">
          {saveErrors(reset.error, t, () => undefined).join(" ")}
        </p>
      )}
      <WidgetGrid
        widgets={grid}
        input={input}
        now={now}
        orgId={orgId}
        actions={actions}
        unavailable={missing}
        needsPlan={(w) => neededPlan(w, page)}
        label={t("devicePage.page")}
        menu={(w) => (missing(w) ? null : <AddToDashboard orgId={orgId} widget={w} deviceName={name} />)}
      />
      <ConfirmDialog
        open={!!resetting}
        title={t(resetting === "organisation" ? "devicePage.resetOrganisationTitle" : "devicePage.resetMineTitle")}
        confirmLabel={t("devicePage.reset")}
        onConfirm={() => resetting && reset.mutate(resetting)}
        onClose={() => setResetting(undefined)}
      >
        <p>{t(resetting === "organisation" ? "devicePage.resetOrganisationText" : "devicePage.resetMineText")}</p>
      </ConfirmDialog>
    </>
  );
}

/**
 * Save asks whose page this is (WEB-DDB-EDIT-01): only for me, or for everyone in the organisation (org.admin). The
 * home page asks the same with its own text.
 */
export function LayerDialog({
  open,
  busy,
  text,
  onPick,
  onClose,
}: {
  open: boolean;
  busy: boolean;
  text?: string;
  onPick: (layer: Layer) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const dialog = useRef<HTMLDialogElement>(null);
  const title = useId();
  useEffect(() => {
    const d = dialog.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    else if (!open && d.open) d.close();
  }, [open]);
  return (
    <dialog
      ref={dialog}
      onClose={onClose}
      aria-labelledby={title}
      className="m-auto w-full max-w-md rounded-lg p-0 shadow-xl backdrop:bg-black/40"
    >
      <div className="p-6">
        <h2 id={title} className="text-lg font-semibold text-gray-900">
          {t("devicePage.saveFor")}
        </h2>
        <p className="mt-3 text-sm text-gray-900">{text ?? t("devicePage.saveForText")}</p>
        <div className="mt-6 flex flex-wrap justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button variant="secondary" disabled={busy} onClick={() => onPick("personal")}>
            {t("devicePage.onlyMe")}
          </Button>
          <Button disabled={busy} onClick={() => onPick("organisation")}>
            {t("devicePage.forEveryone")}
          </Button>
        </div>
      </div>
    </dialog>
  );
}
